import { useState, useEffect, useCallback } from "react";
import { supabase, AttendanceRecord } from "@/lib/supabase";
import { t } from "@/lib/i18n";
import { CalendarDays, Filter, Users, UserCheck, UserX, BarChart3 } from "lucide-react";

export default function AttendanceDashboard() {
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const [personType, setPersonType] = useState<"all" | "student" | "volunteer">("all");
  const [gender, setGender] = useState<"all" | "female" | "male">("all");
  const [level, setLevel] = useState<"all" | "preschool" | "primary" | "secondary">("all");
  const [department, setDepartment] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const departments = [
    "all", "Administration", "Teachers (Pre-School)", "Teachers (Primary)",
    "Psycho-Social", "Media", "School Safety", "Sanitation", "Maintenance",
  ];

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    let query = supabase.from("attendance").select("*").order("record_date", { ascending: false });

    if (personType !== "all") query = query.eq("person_type", personType);
    if (gender !== "all") query = query.eq("gender", gender);
    if (level !== "all") query = query.eq("level", level);
    if (department !== "all") query = query.eq("department", department);
    if (dateFrom) query = query.gte("record_date", dateFrom);
    if (dateTo) query = query.lte("record_date", dateTo);

    const { data } = await query.limit(500);
    if (data) setRecords(data as AttendanceRecord[]);
    setLoading(false);
  }, [personType, gender, level, department, dateFrom, dateTo]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel("attendance-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "attendance" }, () => fetchRecords())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchRecords]);

  const total = records.length;
  const present = records.filter((r) => r.status === "present").length;
  const absent = records.filter((r) => r.status === "absent").length;
  const justified = records.filter((r) => r.justification === "justified").length;

  const byDate: Record<string, AttendanceRecord[]> = {};
  records.forEach((r) => {
    if (!byDate[r.record_date]) byDate[r.record_date] = [];
    byDate[r.record_date].push(r);
  });
  const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  const isWeekend = (dateStr: string) => {
    const day = new Date(dateStr).getDay();
    return day === 0 || day === 6;
  };

  const clearFilters = () => {
    setPersonType("all"); setGender("all"); setLevel("all"); setDepartment("all");
    setDateFrom(""); setDateTo("");
  };

  return (
    <div className="animate-fadeIn">
      <div className="card">
        <div className="mb-5 pb-3 border-b-2 border-gray-200">
          <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold">
            <CalendarDays className="w-6 h-6" /> {t("att_title")}
          </h2>
        </div>
        <p className="text-gray-500 text-sm mb-5">{t("att_desc")}</p>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#F1F5F9] rounded-lg p-4 border border-[#CBD5E1]">
            <div className="flex items-center gap-2 text-[#1C3988] mb-1">
              <BarChart3 className="w-5 h-5" />
              <span className="text-sm font-semibold">{t("lbl_total_records")}</span>
            </div>
            <p className="text-2xl font-bold text-[#1C3988]">{total}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center gap-2 text-[#2E7D32] mb-1">
              <UserCheck className="w-5 h-5" />
              <span className="text-sm font-semibold">{t("lbl_present")}</span>
            </div>
            <p className="text-2xl font-bold text-[#2E7D32]">{present}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <div className="flex items-center gap-2 text-[#D32F2F] mb-1">
              <UserX className="w-5 h-5" />
              <span className="text-sm font-semibold">{t("lbl_absent")}</span>
            </div>
            <p className="text-2xl font-bold text-[#D32F2F]">{absent}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <div className="flex items-center gap-2 text-[#D97706] mb-1">
              <Users className="w-5 h-5" />
              <span className="text-sm font-semibold">{t("lbl_justified")}</span>
            </div>
            <p className="text-2xl font-bold text-[#D97706]">{justified}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#F1F5F9] p-4 rounded-lg border border-[#CBD5E1] mb-5">
          <h3 className="text-[#1C3988] font-bold mb-3 flex items-center gap-2">
            <Filter className="w-5 h-5" /> {t("att_filter_title")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="form-group">
              <label className="form-label">{t("lbl_person_type")}</label>
              <select className="form-input" value={personType} onChange={(e) => setPersonType(e.target.value as any)}>
                <option value="all">{t("lbl_all")}</option>
                <option value="student">{t("opt_students")}</option>
                <option value="volunteer">{t("opt_volunteers_staff")}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_gender")}</label>
              <select className="form-input" value={gender} onChange={(e) => setGender(e.target.value as any)}>
                <option value="all">{t("lbl_all")}</option>
                <option value="female">{t("opt_female")}</option>
                <option value="male">{t("opt_male")}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t("att_lbl_class_level")}</label>
              <select className="form-input" value={level} onChange={(e) => setLevel(e.target.value as any)}>
                <option value="all">{t("lbl_all")}</option>
                <option value="preschool">{t("opt_level_preschool_short")}</option>
                <option value="primary">{t("opt_level_primary_short")}</option>
                <option value="secondary">{t("opt_level_secondary_short")}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t("att_lbl_dept_volunteers")}</label>
              <select className="form-input" value={department} onChange={(e) => setDepartment(e.target.value)}>
                {departments.map((d) => <option key={d} value={d}>{d === "all" ? t("lbl_all") : d}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_date_from")}</label>
              <input className="form-input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_date_to")}</label>
              <input className="form-input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
          <button onClick={clearFilters} className="btn-warning btn-sm mt-3">{t("btn_clear_filters")}</button>
        </div>

        {/* Results grouped by date */}
        {loading ? (
          <p className="text-gray-400 italic py-4">{t("att_loading")}</p>
        ) : sortedDates.length === 0 ? (
          <p className="text-gray-400 italic py-4">{t("att_no_records")}</p>
        ) : (
          <div className="space-y-5">
            {sortedDates.map((date) => {
              const dayRecords = byDate[date];
              const dayPresent = dayRecords.filter((r) => r.status === "present").length;
              const dayAbsent = dayRecords.filter((r) => r.status === "absent").length;
              const weekend = isWeekend(date);
              return (
                <div key={date} className={`border rounded-lg p-4 ${weekend ? "bg-amber-50 border-amber-300" : "bg-white border-[#E2E8F0]"}`}>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h4 className="font-bold text-[#1C3988] flex items-center gap-2">
                      <CalendarDays className="w-5 h-5" />
                      {new Date(date + "T00:00").toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                      {weekend && <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full ml-2">{t("att_weekend")}</span>}
                    </h4>
                    <div className="flex gap-3 text-sm">
                      <span className="text-[#2E7D32] font-semibold">{t("lbl_present")}: {dayPresent}</span>
                      <span className="text-[#D32F2F] font-semibold">{t("lbl_absent")}: {dayAbsent}</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 text-left text-gray-500">
                          <th className="py-2 px-2">{t("lbl_name")}</th>
                          <th className="py-2 px-2">{t("lbl_type")}</th>
                          <th className="py-2 px-2">{t("lbl_gender")}</th>
                          <th className="py-2 px-2">{t("lbl_class_dept")}</th>
                          <th className="py-2 px-2">{t("lbl_level")}</th>
                          <th className="py-2 px-2">{t("lbl_shift")}</th>
                          <th className="py-2 px-2">{t("lbl_status")}</th>
                          <th className="py-2 px-2">{t("lbl_justification")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dayRecords.map((r) => (
                          <tr key={r.id} className="border-b border-gray-100">
                            <td className="py-2 px-2 font-medium">{r.person_name}</td>
                            <td className="py-2 px-2 capitalize">{r.person_type}</td>
                            <td className="py-2 px-2 capitalize">{r.gender || "-"}</td>
                            <td className="py-2 px-2">{r.class_name || r.department || "-"}</td>
                            <td className="py-2 px-2 capitalize">{r.level || "-"}</td>
                            <td className="py-2 px-2 capitalize">{r.shift || "-"}</td>
                            <td className="py-2 px-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${r.status === "present" ? "bg-[#2E7D32] text-white" : "bg-[#D32F2F] text-white"}`}>
                                {r.status}
                              </span>
                            </td>
                            <td className="py-2 px-2 capitalize">{r.justification || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
